"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface UseIdleLogoutOptions {
  idleMs: number;
  warningMs: number;
  enabled: boolean;
  onTimeout: () => void;
}

interface UseIdleLogoutResult {
  isWarning: boolean;
  secondsRemaining: number;
  dismissWarning: () => void;
}

const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "scroll",
  "touchstart",
] as const;

const ACTIVITY_THROTTLE_MS = 500;

/**
 * Tracks user inactivity and triggers a warning followed by a timeout callback.
 *
 * - After `idleMs` of no activity → warning is shown.
 * - The warning counts down for `warningMs`; if no activity → `onTimeout` fires.
 * - Activity, returning from a hidden tab, or `dismissWarning` resets the timer.
 * - When `enabled` is false, no listeners are attached and no timer runs.
 */
export function useIdleLogout({
  idleMs,
  warningMs,
  enabled,
  onTimeout,
}: UseIdleLogoutOptions): UseIdleLogoutResult {
  const [isWarning, setIsWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(
    Math.ceil(warningMs / 1000),
  );

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const onTimeoutRef = useRef(onTimeout);

  // Keep latest onTimeout without retriggering listener effects.
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
    if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);
    idleTimerRef.current = null;
    timeoutTimerRef.current = null;
    warningIntervalRef.current = null;
  }, []);

  const startWarning = useCallback(() => {
    setIsWarning(true);
    setSecondsRemaining(Math.ceil(warningMs / 1000));

    warningIntervalRef.current = setInterval(() => {
      setSecondsRemaining((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    timeoutTimerRef.current = setTimeout(() => {
      clearTimers();
      onTimeoutRef.current();
    }, warningMs);
  }, [warningMs, clearTimers]);

  const resetTimers = useCallback(() => {
    clearTimers();
    setIsWarning(false);
    setSecondsRemaining(Math.ceil(warningMs / 1000));

    if (!enabled) return;

    idleTimerRef.current = setTimeout(() => {
      startWarning();
    }, idleMs);
  }, [enabled, idleMs, warningMs, clearTimers, startWarning]);

  const dismissWarning = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setIsWarning(false);
      return;
    }
    resetTimers();
    return clearTimers;
  }, [enabled, resetTimers, clearTimers]);

  useEffect(() => {
    if (!enabled) return;

    const markActive = () => {
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityRef.current = now;
      resetTimers();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resetTimers();
      }
    };

    const opts: AddEventListenerOptions = { passive: true, capture: true };
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, markActive, opts);
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, markActive, opts);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, resetTimers]);

  return { isWarning, secondsRemaining, dismissWarning };
}
